import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Clear, Draw, Send } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { API_CONFIG } from "../../../../config/api";

const API_BASE = API_CONFIG.BASE_URL.replace(/\/$/, "");

type ConsentStatus = "invited" | "pending" | "completed" | "declined" | "withdrawn" | "expired" | "superseded";

interface ConsentSection {
  key: string;
  title: string;
  bodyHtml?: string;
  body?: string;
}

type CustomAnswerType =
  | "text"
  | "long_text"
  | "number"
  | "date"
  | "yes_no"
  | "single_choice"
  | "multiple_choice";

type CustomAnswerValue = string | string[];

interface ParticipantQuestion {
  id?: string;
  key?: string;
  prompt: string;
  answerType: CustomAnswerType;
  required?: boolean;
  options?: string[];
}

interface ParticipantQuestionSection {
  id?: string;
  key?: string;
  title: string;
  questions?: ParticipantQuestion[];
}

interface PublicConsent {
  consentId: string;
  participantId?: string | null;
  participantLookupKey?: string | null;
  studyName?: string | null;
  status: ConsentStatus;
  formName: string;
  versionNumber?: number;
  versionName?: string | null;
  requireTypedSignature: boolean;
  requireDrawnSignature: boolean;
  formContent: {
    title?: string;
    sections?: ConsentSection[];
    consentStatement?: string;
    participantSections?: ParticipantQuestionSection[];
  };
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function questionKey(question: ParticipantQuestion) {
  return String(question.key || question.id || question.prompt);
}

function inputTypeForQuestion(type: CustomAnswerType) {
  if (type === "number") return "number";
  if (type === "date") return "date";
  return "text";
}

function questionOptions(question: ParticipantQuestion) {
  return Array.isArray(question.options)
    ? question.options.map((option) => String(option || "").trim()).filter(Boolean)
    : [];
}

function hasAnswerValue(value: CustomAnswerValue | undefined) {
  if (Array.isArray(value)) return value.length > 0;
  return String(value || "").trim() !== "";
}

function SignaturePad({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  const drawValueOnCanvas = useCallback((dataUrl: string) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;
    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      }
    };
    img.src = dataUrl;
  }, [dpr]);

  const setupContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111827";
      ctxRef.current = ctx;
    }
  }, [dpr]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);
    if (canvas.width === w && canvas.height === h) return;
    canvas.width = w;
    canvas.height = h;
    setupContext();
  }, [dpr, setupContext]);

  useLayoutEffect(() => {
    resizeCanvas();
    setupContext();
  }, [resizeCanvas, setupContext]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(resizeCanvas);
    ro.observe(canvas.parentElement || canvas);
    return () => ro.disconnect();
  }, [resizeCanvas]);

  useEffect(() => {
    if (!value) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    drawValueOnCanvas(value);
  }, [value, drawValueOnCanvas]);

  const getPoint = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }, []);

  const syncValue = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL("image/png"));
  }, [onChange]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      drawingRef.current = true;
      lastPointRef.current = getPoint(event);
    },
    [disabled, getPoint],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current || disabled) return;
      const previous = lastPointRef.current;
      if (!previous) return;
      const ctx = ctxRef.current;
      if (!ctx) return;
      const next = getPoint(event);
      ctx.beginPath();
      ctx.moveTo(previous.x, previous.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      lastPointRef.current = next;
    },
    [disabled, getPoint],
  );

  const stopDrawing = useCallback(() => {
    drawingRef.current = false;
    lastPointRef.current = null;
    syncValue();
  }, [syncValue]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || disabled) return;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
    onChange("");
  }, [disabled, dpr, onChange]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Draw fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Drawn signature
          </Typography>
        </Stack>
        <Button size="small" startIcon={<Clear />} onClick={clear} disabled={disabled || !value}>
          Clear
        </Button>
      </Stack>
      <Box
        component="canvas"
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDrawing}
        onPointerCancel={stopDrawing}
        sx={{
          width: "100%",
          height: 220,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
          touchAction: "none",
          cursor: disabled
            ? "not-allowed"
            : "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' stroke='black' stroke-width='2'%3E%3Cline x1='12' y1='4' x2='12' y2='20'/%3E%3Cline x1='4' y1='12' x2='20' y2='12'/%3E%3C/svg%3E\") 12 12, crosshair",
        }}
      />
    </Box>
  );
}

export default function ParticipantConsentPage() {
  const { token = "" } = useParams();
  const [consent, setConsent] = useState<PublicConsent | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [signatureDate, setSignatureDate] = useState(todayIso);
  const [drawnSignature, setDrawnSignature] = useState("");
  const [sendCopy, setSendCopy] = useState(false);
  const [emailForCopy, setEmailForCopy] = useState("");
  const [customAnswers, setCustomAnswers] = useState<Record<string, CustomAnswerValue>>({});

  const sections = useMemo(() => consent?.formContent?.sections || [], [consent]);
  const participantSections = useMemo(
    () => consent?.formContent?.participantSections || [],
    [consent],
  );
  const isTerminal = consent?.status === "completed" || consent?.status === "declined";
  const consentTitle = consent
    ? `${consent.studyName || consent.formName || "Study"} Consent Form - Version ${consent.versionNumber || 1}`
    : "Consent Form";

  const loadConsent = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/consent/public/${encodeURIComponent(token)}`);
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Request failed (${response.status})`);
      setConsent(json.consent);
    } catch (err: any) {
      setError(err?.message || "Failed to load consent form.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  useEffect(() => {
    setCustomAnswers({});
  }, [consent?.consentId]);

  const submit = async () => {
    if (!consent || submitting) return;
    if (!agreed) {
      setError("Please confirm that you agree before submitting.");
      return;
    }
    if (!typedSignature.trim()) {
      setError("Please type your full name as your electronic signature.");
      return;
    }
    if (consent.requireDrawnSignature && !drawnSignature) {
      setError("Please draw your signature before submitting.");
      return;
    }
    for (const section of participantSections) {
      for (const question of section.questions || []) {
        const key = questionKey(question);
        if (question.required && !hasAnswerValue(customAnswers[key])) {
          setError(`Please answer the required question: ${question.prompt}`);
          return;
        }
      }
    }
    const sendCopyEmail = sendCopy ? emailForCopy.trim() : "";
    if (sendCopy && !sendCopyEmail) {
      setError("Please enter your email address to receive a copy.");
      return;
    }
    if (sendCopy && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sendCopyEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    const customAnswerItems = participantSections.flatMap((section) =>
      (section.questions || []).map((question) => ({
        questionKey: questionKey(question),
        questionLabel: question.prompt,
        answerValue: customAnswers[questionKey(question)] ?? "",
      })),
    );
    const allAnswers = [
      ...customAnswerItems,
      { questionKey: "consent_agreement", questionLabel: "Consent agreement", answerValue: agreed },
      { questionKey: "typed_signature", questionLabel: "Typed signature", answerValue: typedSignature.trim() },
      { questionKey: "signature_date", questionLabel: "Signature date", answerValue: signatureDate },
    ];

    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/consent/public/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agreed,
            typedSignature: typedSignature.trim(),
            signatureDate,
            signatureImageUrl: drawnSignature || null,
            sendCopyToEmail: sendCopyEmail || null,
            answers: allAnswers,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Request failed (${response.status})`);
      setConsent((current) => (current ? { ...current, status: "completed" } : current));
      setShowSuccessDialog(true);
    } catch (err: any) {
      setError(err?.message || "Failed to submit consent.");
    } finally {
      setSubmitting(false);
    }
  };

  const decline = async () => {
    if (!consent || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/consent/public/${encodeURIComponent(token)}/decline`, {
        method: "POST",
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Request failed (${response.status})`);
      setConsent((current) => (current ? { ...current, status: "declined" } : current));
      setShowSuccessDialog(true);
    } catch (err: any) {
      setError(err?.message || "Failed to decline consent.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 2.5, md: 4 }, border: 1, borderColor: "divider" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNotice("")}>
            {notice}
          </Alert>
        )}

        {!consent ? (
          <Typography>Consent form is not available.</Typography>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {consent.studyName || "StudySync study"}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {consentTitle}
              </Typography>
              {consent.versionName && (
                <Typography variant="body2" color="text.secondary">
                  Reference: {consent.versionName}
                </Typography>
              )}
            </Box>

            {isTerminal && (
              <Alert severity={consent.status === "completed" ? "success" : "warning"} icon={<CheckCircle />}>
                {consent.status === "completed"
                  ? "This consent form has already been completed."
                  : "This consent form has been declined."}
              </Alert>
            )}

            {sections.map((section) => (
              <Box key={section.key}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    color: "text.primary",
                    "& p": { mt: 0, mb: 1.25 },
                    "& ul": { pl: 3 },
                  }}
                  dangerouslySetInnerHTML={{ __html: section.bodyHtml || section.body || "" }}
                />
              </Box>
            ))}

            {participantSections.some((section) => (section.questions || []).length > 0) && (
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Participant questions
                </Typography>
                {participantSections.map((section) => {
                  const questions = section.questions || [];
                  if (questions.length === 0) return null;
                  return (
                    <Box key={section.key || section.id || section.title}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {section.title}
                      </Typography>
                      <Stack spacing={1.5}>
                        {questions.map((question) => {
                          const key = questionKey(question);
                          const type = inputTypeForQuestion(question.answerType);
                          const value = customAnswers[key];
                          const options = questionOptions(question);
                          const updateAnswer = (next: CustomAnswerValue) =>
                            setCustomAnswers((current) => ({ ...current, [key]: next }));
                          const toggleOption = (option: string, checked: boolean) => {
                            const currentValues = Array.isArray(value) ? value : [];
                            updateAnswer(
                              checked
                                ? [...currentValues, option]
                                : currentValues.filter((item) => item !== option),
                            );
                          };

                          return (
                            <Box key={key}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                                {question.prompt}{question.required ? " *" : ""}
                              </Typography>
                              {question.answerType === "yes_no" ? (
                                <FormControl disabled={isTerminal} required={question.required}>
                                  <RadioGroup
                                    value={typeof value === "string" ? value : ""}
                                    onChange={(event) => updateAnswer(event.target.value)}
                                  >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                  </RadioGroup>
                                </FormControl>
                              ) : question.answerType === "single_choice" ? (
                                <FormControl disabled={isTerminal} required={question.required}>
                                  <RadioGroup
                                    value={typeof value === "string" ? value : ""}
                                    onChange={(event) => updateAnswer(event.target.value)}
                                  >
                                    {options.map((option) => (
                                      <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              ) : question.answerType === "multiple_choice" ? (
                                <FormControl disabled={isTerminal} required={question.required}>
                                  <FormGroup>
                                    {options.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        control={
                                          <Checkbox
                                            checked={Array.isArray(value) && value.includes(option)}
                                            onChange={(event) => toggleOption(option, event.target.checked)}
                                          />
                                        }
                                        label={option}
                                      />
                                    ))}
                                  </FormGroup>
                                </FormControl>
                              ) : (
                                <TextField
                                  value={typeof value === "string" ? value : ""}
                                  onChange={(event) => updateAnswer(event.target.value)}
                                  type={type}
                                  multiline={question.answerType === "text" || question.answerType === "long_text"}
                                  rows={question.answerType === "long_text" ? 4 : question.answerType === "text" ? 3 : undefined}
                                  required={question.required}
                                  disabled={isTerminal}
                                  fullWidth
                                  placeholder="Your answer"
                                  InputLabelProps={question.answerType === "date" ? { shrink: true } : undefined}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
            <Divider />

            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Consent and signature
              </Typography>
              <Typography variant="body1">{consent.formContent.consentStatement}</Typography>
              <FormControlLabel
                control={<Checkbox checked={agreed} onChange={(event) => setAgreed(event.target.checked)} disabled={isTerminal} />}
                label="I have read and understand this consent form, and I agree to participate."
              />
              <TextField
                label="Typed signature"
                value={typedSignature}
                onChange={(event) => setTypedSignature(event.target.value)}
                disabled={isTerminal}
                fullWidth
                required
              />
              <TextField
                label="Signature date"
                type="date"
                value={signatureDate}
                onChange={(event) => setSignatureDate(event.target.value)}
                disabled={isTerminal}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              {consent.requireDrawnSignature && (
                <SignaturePad value={drawnSignature} onChange={setDrawnSignature} disabled={isTerminal} />
              )}
            </Stack>

            {!isTerminal && (
              <>
                <FormControlLabel
                  control={<Checkbox checked={sendCopy} onChange={(event) => setSendCopy(event.target.checked)} />}
                  label="Email me a copy of this signed consent form"
                />
                {sendCopy && (
                  <TextField
                    label="Email address"
                    type="email"
                    value={emailForCopy}
                    onChange={(event) => setEmailForCopy(event.target.value)}
                    fullWidth
                    required
                    placeholder="you@example.com"
                  />
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
                  <Button color="warning" variant="outlined" onClick={decline} disabled={submitting}>
                    Decline
                  </Button>
                  <Button variant="contained" endIcon={<Send />} onClick={submit} disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit consent"}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Paper>

      <Dialog
        open={showSuccessDialog}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            textAlign: "center",
            py: 2,
            position: "fixed",
            top: "30%",
            m: 0,
          },
        }}
      >
        <DialogContent>
          <CheckCircle sx={{ fontSize: 56, color: "success.main", mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {consent?.status === "declined" ? "Response recorded" : "Form collected"}
          </Typography>
          <DialogContentText>
            {consent?.status === "declined"
              ? "Your decision has been recorded. You may close this window."
              : "Thank you! Your consent form has been submitted successfully. You may close this window."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button variant="contained" onClick={() => window.close()}>
            Close window
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
